import { getAppServices } from "@10x/coreapp";
import { RESET, SET_REQUEST } from "./core/actions";
import { BaseEjariContract, EjariProperty, EjariPropertyType, Lookup } from "@eres/ejari-api/Entity";
import {
	HTAttachment,
	HTDamage,
	HTDocLookup,
	HTLookup,
	HTParty,
	HTProperty,
	HTRequest,
	HTRequestStatus,
	HTRequestType,
	HTRoleType,
	YES
} from "./api/entities";
import { getLastContract, LookupProcessor } from "@eres/ejari-api/Utils";
import { TabuProfile } from "@10x/sso/TabuProfile";
import { getParticipantID } from "@10x/sso";
import { DateUtil, LocaleUtil } from "@eres/core";
import { REQ_SOURCE_DLD_WEB } from "@eres/rtregistrationapi/entities";
import { HTLookUps } from "./api/constants";
import { TabuDocTypes } from "@my-dld/utils/TabuUtils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { loadLookUp, uploadDoc } from "./api/api";
import Strings from "@my-dld/assets/strings";
import { DescriptionData } from "./web/components/Description";
import { DamageData } from "./web/components/Damages";
import { validateResponseX } from "@10x/rentalvaluation/api/api";
import { getLocalValue } from "@eres/core/dist/LocaleValue";

export function resetData() {
	getAppServices().storeService.getStore().dispatch( {
		type: RESET,
	} );
}

export function setData( property: EjariProperty, type: HTRequestType ) {
	const request = getHTRequest( property, type ) as HTRequest;
	setRequest( request );
}

export function setRequest( request: HTRequest ) {
	getAppServices().storeService.getStore().dispatch( {
		type: SET_REQUEST,
		payload: request,
	} );
}

function getHTRequest( ejariProperty: EjariProperty, type: HTRequestType ): Partial<HTRequest> {
	const lastContract = getLastContract( ejariProperty )!;
	const property = getHTProperty( ejariProperty, lastContract ) as HTProperty;
	const owner = getHTOwner() as HTParty;
	const tenant = getHTTenant( lastContract ) as HTParty;
	return {
		Identity: 0,
		Properties: [ property ],
		ContractNumber: property.ContractNumber,
		ParticipantId: getParticipantID(),
		Parties: [ owner, tenant ],
		UserAccountId: owner.UserAccountId,
		TenancyContractId: lastContract.ContractId,
		RequestedOn: DateUtil.toServerDate( new Date() ),
		RequestTypeId: type,
		RequestChannelId: REQ_SOURCE_DLD_WEB,
		PaidSecurityDeposit: lastContract.PaidSecurityDeposit,
	};
}

export function getHTProperty( eProperty: EjariProperty, lastContract: BaseEjariContract ): Partial<HTProperty> {
	return {
		AreaId: eProperty.LandAreaId,
		AreaNameEn: eProperty.AreaEn,
		AreaNameAr: eProperty.AreaAr,
		IsRented: eProperty.IsRented ? 1 : 0,
		BuildingEjPropertyId: Number( eProperty.BuildingEjariPropertyId ),
		BuildingNameAr: eProperty.BuildingNameAr,
		BuildingNameEn: eProperty.BuildingNameEn,
		ContractAnnualAmount: lastContract.ContractAmount,
		ContractEndDate: lastContract.ContractEndDate,
		ContractStartDate: lastContract.ContractStartDate,
		ContractNumber: lastContract.ContractNumber,
		DewaNumber: eProperty.Dewa,
		LandEjPropertyId: Number( eProperty.LandEjariPropertyId ),
		PropertySize: Number( eProperty.Size ),
		UnitEjPropertyId: Number( eProperty.UnitEjariPropertyId ),
		VillaEjPropertyId: Number( eProperty.VillaEjariPropertyId ),
		EjariSubTypeId: eProperty.EjariSubTypeId,
		EjariSubTypeNameAr: eProperty.EjariSubTypeNameAr,
		EjariSubTypeNameEn: eProperty.EjariSubTypeNameEn,
		EjariTypeId: eProperty.EjariTypeId,
		EjariTypeNameAr: eProperty.EjariTypeNameAr,
		EjariTypeNameEn: eProperty.EjariTypeNameEn,
		EjariUsageId: eProperty.EjariUsageId,
		EjariUsageNameAr: eProperty.EjariUsageNameAr,
		EjariUsageNameEn: eProperty.EjariUsageNameEn,
		Identity: eProperty.Identity,
		IsFreehold: eProperty.IsFreeHold ? 1 : 0,
		LandDmNo: eProperty.LandDmNo,
		LandDmSubNo: eProperty.LandDmSubNo,
		LandNo: eProperty.LandNo,
		LandPropertyId: eProperty.LandPropertyId,
		LandSubNo: eProperty.LandSubNo,
		PropertyId: eProperty.PropertyId,
		PropertyNumber: eProperty.PropertyNumber,
		PropertyTypeId: eProperty.PropertyTypeId,
		UnitPropertyId: eProperty.UnitPropertyId,
		VillaPropertyId: eProperty.VillaPropertyId,
	};
}

export function getHTTenant( contract: BaseEjariContract ): Partial<HTParty> {
	return {
		Identity: 0,
		MobileNumber: contract.ContractTenantMobile,
		Email: contract.ContractTenantEmail,
		EmiratesId: contract.ContractTenantEmiratesId,
		LicenseNumber: contract.LicenseNumber,
		ManagementCompId: Number( contract.ManagementCompanyId ),
		PartNameAr: contract.ContractTenantNameAr,
		PartNameEn: contract.ContractTenantNameEn,
		RoleId: HTRoleType.TENANT,
		TenantId: contract.ContractTenantId,
		UnifiedIdNumber: contract.ContractTenantUNumber,
		IsOwnerCompany: false,
	};
}

export function getHTOwner(): Partial<HTParty> {
	const user = TabuProfile.getProfile();
	return {
		Identity: 0,
		Email: user?.emailAddress,
		MobileNumber: user?.phoneNumber,
		EmiratesId: user?.emiratesId,
		PartNameAr: user?.nameAr,
		PartNameEn: user?.nameEn,
		RoleId: HTRoleType.OWNER,
		ParticipantId: user?.tabuParticipantId,
		UserAccountId: user?.ssoUserId,
		IsEjariUser: 0,
		IsOwnerCompany: false,
	};
}

interface LookupConsumer {
	onLookup: ( lookup: HTLookUps | string, values: Lookup[] ) => void;
}

export function populateLookup( lookup: HTLookUps | string, consumer?: LookupConsumer, processor?: LookupProcessor ) {
	loadLookUp( lookup ).then( lookups => {
		if ( processor )
			lookups = processor( lookups );
		if ( consumer ) {
			consumer.onLookup( lookup, lookups );
		}
	} ).catch( e => {
		console.error( "Loading lookup failed ", lookup, e );
	} );
}

export function getDocType( doc: HTAttachment ) {
	let type = TabuDocTypes.PDF;
	let fileUrl = doc.FileUrl;
	let localData = doc.LocalData;
	if ( fileUrl && !isEmptyStringOrNull( fileUrl ) ) {
		fileUrl = fileUrl.toLocaleLowerCase();
		if ( fileUrl.endsWith( ".png" ) || fileUrl.endsWith( ".jpg" ) )
			type = TabuDocTypes.IMAGE;
	} else if ( localData && !isEmptyStringOrNull( localData ) ) {
		const d = localData.split( ";" )[ 0 ];
		if ( d.includes( "image" ) )
			type = TabuDocTypes.IMAGE;
	}
	return type;
}

export function getDocName( docLookup: HTDocLookup, doc: HTAttachment ) {
	let fileUrl = doc.FileUrl;
	if ( fileUrl && !isEmptyStringOrNull( fileUrl ) ) {
		const ext = getDocExtension( fileUrl );
		return `${ getLocalValue( docLookup ) }_~.${ ext }`;
	}
	return doc.PhysicalName;
}

export function getDocExtension( fileUrl: string ) {
	if ( fileUrl && !isEmptyStringOrNull( fileUrl ) )
		return fileUrl.substring( fileUrl.lastIndexOf( "." ) + 1, fileUrl.length );
	return "";
}

export function getDocSize( doc: HTAttachment ) {
	const size = doc.ActualSizeKb;
	if ( size && size > 0 ) {
		return ( size / 1024 ).toFixed( 2 );
	}
	return "";
}

export function extractPropInfo( request?: HTRequest ) {
	let icon = "";
	let val = "";
	let lbl = "";
	if ( request && request.Properties && request.Properties.length > 0 ) {
		const property = request.Properties[ 0 ];
		switch ( property.PropertyTypeId ) {
			case EjariPropertyType.LAND:
				let lm = property.LandNo;
				if ( property.LandSubNo && !isEmptyStringOrNull( property.LandSubNo ) && "0" !== property.LandSubNo ) {
					lm = lm + "-" + property.LandSubNo;
				}
				val = property.LandNo;
				lbl = Strings.land_num_label;
				icon = "land";
				break;
			case EjariPropertyType.UNIT:
				val = property.PropertyNumber;
				lbl = Strings.Unit_Number_Label;
				icon = "unit";
				break;
			case EjariPropertyType.VILLA:
				val = LocaleUtil.getLocalValue( { en: property.BuildingNameEn, ar: property.BuildingNameAr } );
				lbl = Strings.villa_name;
				icon = "villa";
				break;
			case EjariPropertyType.BUILDING:
				val = LocaleUtil.getLocalValue( { en: property.BuildingNameEn, ar: property.BuildingNameAr } );
				lbl = Strings.building_name;
				icon = "building";
				break;
		}
	}
	return { val, lbl, icon };
}

export function onDocDrop( doc: HTDocLookup, files: File[], onFinish: ( sDoc: HTDocLookup ) => void ) {
	if ( doc.MaxPages > 1 ) {
		if ( doc.docs && doc.docs.length > 0 ) {
			let maxAllowed = ( doc.MaxPages - doc.docs.length );
			if ( files.length > maxAllowed )
				files = files.splice( 0, maxAllowed );
		} else if ( files.length > doc.MaxPages ) {
			files = files.splice( 0, doc.MaxPages );
		}
	}
	files.forEach( ( file: File ) => {
		if ( file.type.startsWith( "image/" ) || file.type.startsWith( "application/pdf" ) ) {
			addFile( doc, file, onFinish );
		}
	} );
}

function addFile( sDoc: HTDocLookup, file: File, onFinish: ( sDoc: HTDocLookup ) => void ) {
	const reader = new FileReader();
	reader.onload = () => {
		const binaryStr = reader.result;
		const attachment: HTAttachment = {
			ActualSizeKb: ( file.size / 1000 ), //Converting into KB
			DocumentId: sDoc.Identity,
			PhysicalName: file.name,
			LocalData: binaryStr as string,
		};
		if ( sDoc.MaxPages > 1 ) {
			if ( sDoc.docs == undefined ) {
				sDoc.docs = [];
			}
		} else {
			sDoc.docs = [];
		}
		sDoc.docs.push( attachment );
		onFinish( sDoc );
	};
	reader.readAsDataURL( file );
}

export function mapLookupWithAttachments( docs: HTDocLookup[], attachments?: HTAttachment[] ) {
	if ( attachments && attachments.length > 0 ) {
		docs.forEach( ( doc: HTDocLookup ) => {
			attachments.forEach( ( attachment: HTAttachment ) => {
				if ( doc.Identity === attachment.DocumentId ) {
					if ( doc.docs == undefined )
						doc.docs = [];
					doc.docs.push( attachment );
				}
			} );
		} );
	}
}

export function buildRegStatus( request: HTRequest, statusId: number ) {
	const property = request.Properties[ 0 ];
	property.FurnishingStatusId = statusId;
}

export function buildRegDescription( request: HTRequest, description: DescriptionData ) {
	const property = request.Properties[ 0 ];
	request.Notes = description.notes || "";
	property.BuildingCards = Number( description.parking );
	property.UnitKeys = description.unitKeys || "";
	if ( request.RequestTypeId === HTRequestType.HANDOVER ) {
		request.PaidSecurityDeposit = Number( description.amount );
	}
	if ( request.RequestTypeId === HTRequestType.TAKEOVER ) {
		property.PropertyConditionId = description.status?.Id;
	}
}

export function buildRegHandoverDocs( request: HTRequest, documents: HTDocLookup[] ): Promise<HTRequest> {
	return new Promise( ( resolve ) => {
		uploadDocs( documents ).then( ( attachments: HTAttachment[] ) => {
			request.HandoverRequestAttachments = attachments;
			resolve( request );
		} );
	} );
}

export function buildRegRequirements( request: HTRequest, requirements: HTLookup[] ) {
	request.FinalizedRequirements = requirements;
}

export function buildRegDamages( request: HTRequest, damageData: DamageData ): Promise<HTRequest> {
	return new Promise( ( resolve, reject ) => {
		let damageId = 0;
		if ( request.Damages && request.Damages.length > 0 ) {
			damageId = request.Damages[ 0 ].Identity || 0;
		}
		const isDamage = damageData.damage?.Id === YES.Id;
		request.IsUnitDamaged = isDamage;
		if ( isDamage ) {
			uploadDocs( damageData.documents ).then( ( attachments: HTAttachment[] ) => {
				const damage: HTDamage = {
					Identity: damageId,
					Description: damageData.description,
					DamageAttachments: attachments,
				};
				request.Damages = [ damage ];
				resolve( request );
			} ).catch( reason => {
				reject( reason );
			} );
		} else {
			request.Damages = [];
			resolve( request );
		}
	} );
}

export function uploadDocs( docs: HTDocLookup[] ): Promise<HTAttachment[]> {
	const uploadedDoc: any[] = [];
	for ( const reqDoc of docs ) {
		if ( reqDoc.docs && reqDoc.docs.length > 0 ) {
			for ( const attachment of reqDoc.docs ) {
				if ( attachment.LocalData && isEmptyStringOrNull( attachment.FileUrl ) ) {
					uploadedDoc.push( uploadDoc( reqDoc.Identity!, attachment )
					.then( validateResponseX ).then( resp => {
						return Promise.resolve( {
							...attachment,
							LocalData: undefined,
							Identity: attachment.Identity ? attachment.Identity : 0,
							FileUrl: resp.Url,
							TemporaryRequestId: resp.TemporaryRequestId,
						} );
					} ).catch( ( reason ) => {
						return Promise.reject( reason );
					} ) );
				} else {
					uploadedDoc.push( attachment );
				}
			}
		}
	}
	return Promise.all( uploadedDoc );
}

export function successMessage( requestType: number, actionId?: number ): string {
	const request = requestType === HTRequestType.HANDOVER ? Strings.ejari_handover : Strings.ejari_takeover;
	switch ( actionId ) {
		case HTRequestStatus.DRAFT :
			return Strings.ht_draft_success.replace( "~", request );
		case HTRequestStatus.SUBMITTED :
			return Strings.ht_submit_success.replace( "~", request );
		case HTRequestStatus.ACCEPTED :
			return Strings.ht_accept_success.replace( "~", request );
		case HTRequestStatus.REJECTED :
			return Strings.ht_rejected_success.replace( "~", request );
		case HTRequestStatus.CANCELLED :
			return Strings.req_cancelled_sucess;
		default:
			return "";
	}
}
